<template>
  <div id="device-transfers">
    <v-card class="mt-4">
      <v-card-title>Initiate Transfer</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-autocomplete
              v-model="selectedHardwareIds"
              :search-input.sync="deviceSearchInput"
              :items="hardwareIdOptions"
              :filter="filterDeviceOption"
              :menu-props="{ contentClass: 'transfer-device-menu' }"
              :auto-select-first="hasSingleExactMatch"
              item-text="text"
              item-value="value"
              label="Select Device(s)"
              hint="Search by Hardware ID, Vehicle ID, Device ID or Tags"
              persistent-hint
              chips
              clearable
              deletable-chips
              multiple
              outlined
              @change="deviceSearchInput = null"
            >
              <template v-slot:item="{ item, on, attrs }">
                <v-list-item v-bind="attrs" class="py-3 transfer-device-option" v-on="on">
                  <v-list-item-action class="my-0 me-3">
                    <v-icon :color="selectedHardwareIds.includes(item.value) ? 'primary' : ''">
                      {{
                        selectedHardwareIds.includes(item.value)
                          ? icons.mdiCheckboxMarked
                          : icons.mdiCheckboxBlankOutline
                      }}
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">{{ item.text }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.subtitle" class="mt-1">
                      {{ item.subtitle }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              :value="senderOrganisation || 'Unknown'"
              label="Transfer initiated by (Sender)"
              outlined
              dense
              readonly
              :error="!senderOrganisation"
              :error-messages="senderOrganisation ? [] : ['Your account is not associated with a known organisation']"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-select
              v-model="receiverOrganisation"
              :items="receiverOptions"
              label="Receiving Organisation (Receiver)"
              outlined
              dense
            ></v-select>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field :value="currentUserEmail" label="Updated By (Email)" outlined dense readonly></v-text-field>
          </v-col>

          <v-col cols="12">
            <v-textarea v-model="statusDetails" label="Device Status / Notes" outlined dense rows="3"></v-textarea>
          </v-col>

          <v-col cols="12" class="d-flex justify-end">
            <v-btn color="primary" :loading="isSubmitting" :disabled="submitDisabled" @click="initiateTransfer">
              {{ editingTransferId ? "Save Changes" : "Initiate Transfer" }}
            </v-btn>
            <v-btn v-if="editingTransferId" class="ml-2" outlined @click="cancelEdit">Cancel</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="mt-6">
      <v-card-title>
        Pending Transfers
        <v-spacer></v-spacer>
        <v-chip small color="warning" outlined>{{ pendingTransfers.length }}</v-chip>
      </v-card-title>
      <v-data-table
        :headers="pendingHeaders"
        :items="pendingTransfers"
        :sort-by.sync="pendingSortBy"
        :sort-desc.sync="pendingSortDesc"
        :must-sort="true"
        no-data-text="No pending transfers"
      >
        <template v-slot:item.hardwareIds="{ item }">
          <div class="d-flex flex-wrap" style="gap: 4px;">
            <v-chip v-for="hwId in item.hardwareIds" :key="hwId" small outlined>{{ hwId }}</v-chip>
          </div>
        </template>
        <template v-slot:item.initiatedAt="{ item }">
          <span class="text-no-wrap">{{ formatDate(item.initiatedAt) }}</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            small
            color="success"
            class="me-2"
            :disabled="!canAccept(item)"
            :title="canAccept(item) ? '' : 'Only the receiving organisation can accept'"
            @click="acceptTransfer(item)"
          >
            Accept
          </v-btn>
          <v-btn
            small
            color="error"
            class="me-2"
            outlined
            :disabled="!canModify(item)"
            :title="canModify(item) ? '' : 'Only sender or receiver can reject'"
            @click="rejectTransfer(item)"
          >
            Reject
          </v-btn>
          <v-btn
            small
            icon
            :disabled="!canModify(item)"
            :title="canModify(item) ? 'Edit' : 'Only sender or receiver can edit'"
            @click="editTransfer(item)"
          >
            <v-icon small>{{ icons.mdiSquareEditOutline }}</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-card class="mt-6 mb-6">
      <v-card-title>
        Completed Transfers
        <v-spacer></v-spacer>
        <v-chip small color="info" outlined>{{ completedTransfers.length }}</v-chip>
      </v-card-title>
      <v-data-table
        :headers="completedHeaders"
        :items="completedTransfers"
        :sort-by.sync="completedSortBy"
        :sort-desc.sync="completedSortDesc"
        :must-sort="true"
        no-data-text="No completed transfers"
      >
        <template v-slot:item.hardwareIds="{ item }">
          <div class="d-flex flex-wrap" style="gap: 4px;">
            <v-chip v-for="hwId in item.hardwareIds" :key="hwId" small outlined>{{ hwId }}</v-chip>
          </div>
        </template>
        <template v-slot:item.initiatedAt="{ item }">
          <span class="text-no-wrap">{{ formatDate(item.initiatedAt) }}</span>
        </template>
        <template v-slot:item.transferStatusDate="{ item }">
          <span class="text-no-wrap">{{ formatDate(item.transferStatusDate) }}</span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip small :color="item.status === 'accepted' ? 'success' : 'error'" label>
            {{ item.status === "accepted" ? "Accepted" : "Rejected" }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { mdiSquareEditOutline, mdiCheckboxMarked, mdiCheckboxBlankOutline } from "@mdi/js"
import { mapState } from "vuex"
import axios from "axios"
import dayjs from "dayjs"
import store from "@/store"
import { getConfigTags, getNewSsdDate, isNewSsdTag } from "@/utils/tags"

const ORGANISATIONS = ["Radiola", "Quickstart", "Somar", "GWRC"]
const TRANSFER_HISTORY_DAYS = 90

export default {
  name: "Transfers",
  metaInfo: {
    title: "Transfers",
  },
  data() {
    return {
      receiverOrganisation: null,
      statusDetails: "",
      deviceSearchInput: "",
      isSubmitting: false,
      isLoading: false,
      editingTransferId: null,
      transfers: [],
      transfersRequest: axios.CancelToken.source(),
      pendingSortBy: "receiving_organisation",
      pendingSortDesc: false,
      completedSortBy: "transferStatusDate",
      completedSortDesc: true,
      pendingHeaders: [
        { text: "Initiated", value: "initiatedAt" },
        { text: "Sender", value: "sender_organisation" },
        { text: "Receiver", value: "receiving_organisation" },
        { text: "Devices", value: "hardwareIds", sortable: false },
        { text: "Location Status", value: "location_status" },
        { text: "Actions", value: "actions", sortable: false, align: "end" },
      ],
      completedHeaders: [
        { text: "Initiated", value: "initiatedAt" },
        { text: "Completed", value: "transferStatusDate" },
        { text: "Sender", value: "sender_organisation" },
        { text: "Receiver", value: "receiving_organisation" },
        { text: "Devices", value: "hardwareIds", sortable: false },
        { text: "Location Status", value: "location_status" },
        { text: "Status", value: "status" },
      ],
      icons: {
        mdiSquareEditOutline,
        mdiCheckboxMarked,
        mdiCheckboxBlankOutline,
      },
    }
  },
  computed: {
    ...mapState({
      devices: state => state.gtfs.devices,
      persistedHardwareIds: state => state.selected_device_hardware_ids,
    }),
    selectedHardwareIds: {
      get() {
        return this.persistedHardwareIds
      },
      set(newIds) {
        this.$store.commit("SET_SELECTED_DEVICE_HARDWARE_IDS", newIds)
      },
    },
    hardwareIdOptions() {
      const seen = new Set()
      const options = []
      this.devices.forEach(device => {
        const caseId = device?.config?.case_id
        if (!caseId || seen.has(caseId)) return
        seen.add(caseId)

        const vehicleId = device.config.vehicle_id
        const deviceId = device.device_id
        const tags = getConfigTags(device.config)

        // New SSD carries the date the drive was swapped, which is worth seeing
        // when picking devices to transfer
        const newSsdDate = getNewSsdDate(device.config)
        const tagLabels = tags.map(tag => (isNewSsdTag(tag) && newSsdDate ? `${tag} (${newSsdDate})` : tag))

        const parts = []
        if (vehicleId) parts.push(`Vehicle ${vehicleId}`)
        if (deviceId) parts.push(`Device ${deviceId}`)
        if (tagLabels.length) parts.push(`Tags: ${tagLabels.join(", ")}`)

        options.push({
          text: caseId,
          value: caseId,
          subtitle: parts.join(" • "),
          searchHaystack: [caseId, vehicleId, deviceId, ...tags].filter(Boolean).join(" ").toLowerCase(),
        })
      })

      return options.sort((a, b) => a.text.localeCompare(b.text))
    },
    hasSingleExactMatch() {
      const query = (this.deviceSearchInput || "").trim().toLowerCase()
      if (!query) return false
      const exact = this.hardwareIdOptions.filter(option => option.text.toLowerCase() === query)

      return exact.length === 1
    },
    senderOrganisation() {
      return this.$auth?.user?.organisation || null
    },
    receiverOptions() {
      return ORGANISATIONS
    },
    currentUserEmail() {
      return this.$auth?.user?.email || ""
    },
    userOrganisation() {
      return this.$auth?.user?.organisation || null
    },
    submitDisabled() {
      return (
        !this.selectedHardwareIds.length || !this.receiverOrganisation || !this.senderOrganisation || this.isSubmitting
      )
    },
    pendingTransfers() {
      return this.transfers
        .filter(t => t.status === "pending")
        .slice()
        .sort((a, b) => {
          const receiverCmp = (a.receiving_organisation || "").localeCompare(b.receiving_organisation || "")
          if (receiverCmp !== 0) return receiverCmp

          return new Date(a.initiatedAt) - new Date(b.initiatedAt)
        })
    },
    completedTransfers() {
      return this.transfers
        .filter(t => t.status === "accepted" || t.status === "rejected")
        .slice()
        .sort((a, b) => new Date(b.initiatedAt) - new Date(a.initiatedAt))
    },
  },
  created() {
    this.fetchTransfers()
  },
  beforeDestroy() {
    this.transfersRequest.cancel("Component destroyed")
  },
  methods: {
    async fetchTransfers() {
      this.transfersRequest.cancel("New fetch requested")
      this.transfersRequest = axios.CancelToken.source()
      this.isLoading = true

      const startDate = dayjs().subtract(TRANSFER_HISTORY_DAYS, "day").format("YYYY-MM-DD 00:00:00")
      const endDate = dayjs().format("YYYY-MM-DD 23:59:59")

      try {
        const rows = await store.dispatch("getDeviceLocationChanges", {
          start_datetime: startDate,
          end_datetime: endDate,
          cancelToken: this.transfersRequest.token,
        })

        this.transfers = this.groupTransferRows(Array.isArray(rows) ? rows : [])
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error(error)
        }
      } finally {
        this.isLoading = false
      }
    },
    groupTransferRows(rows) {
      const grouped = new Map()
      rows.forEach(row => {
        const transferStatus = (row.transfer_status || "").toLowerCase()
        if (!transferStatus) return

        const key = row.id || `${row.created}|${row.user}|${row.sender_organisation}|${row.receiving_organisation}`
        if (!grouped.has(key)) {
          grouped.set(key, {
            id: row.id || key,
            hardwareIds: [],
            sender_organisation: row.sender_organisation || null,
            receiving_organisation: row.receiving_organisation || null,
            location_status: row.location_status || "",
            initiatedBy: row.user || "",
            initiatedAt: row.created || null,
            status: transferStatus,
            transferStatusDate: row.transfer_status_date || null,
          })
        }
        const transfer = grouped.get(key)
        if (row.case_id && !transfer.hardwareIds.includes(row.case_id)) {
          transfer.hardwareIds.push(row.case_id)
        }
      })

      return Array.from(grouped.values())
    },
    filterDeviceOption(item, queryText) {
      if (!queryText) return true

      return item.searchHaystack.includes(queryText.toLowerCase())
    },
    formatDate(value) {
      if (!value) return "-"
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value

      return date.toLocaleString()
    },
    canAccept(transfer) {
      return !!this.userOrganisation && transfer.receiving_organisation === this.userOrganisation
    },
    canModify(transfer) {
      if (!this.userOrganisation) return false

      return (
        transfer.sender_organisation === this.userOrganisation ||
        transfer.receiving_organisation === this.userOrganisation
      )
    },
    resetForm() {
      this.receiverOrganisation = null
      this.statusDetails = ""
      this.editingTransferId = null
      this.$store.commit("SET_SELECTED_DEVICE_HARDWARE_IDS", [])
    },
    cancelEdit() {
      this.resetForm()
    },
    editTransfer(transfer) {
      if (!this.canModify(transfer)) return
      this.editingTransferId = transfer.id
      this.receiverOrganisation = transfer.receiving_organisation
      this.statusDetails = transfer.location_status || ""
      this.$store.commit("SET_SELECTED_DEVICE_HARDWARE_IDS", transfer.hardwareIds)
    },
    async initiateTransfer() {
      if (!this.senderOrganisation) {
        this.$store.commit("SET_SNACKBAR", {
          text: "Your account is not associated with a known organisation",
          color: "error",
        })

        return
      }

      this.isSubmitting = true

      try {
        await store.dispatch("updateDeviceLocations", {
          id: this.editingTransferId || undefined,
          hardware_id: [...this.selectedHardwareIds],
          status: this.statusDetails,
          user: this.currentUserEmail,
          sender_organisation: this.senderOrganisation,
          receiving_organisation: this.receiverOrganisation,
          transfer_status: "Pending",
        })

        this.resetForm()
        await this.fetchTransfers()
      } catch (error) {
        console.error(error)
      }

      this.isSubmitting = false
    },
    async acceptTransfer(transfer) {
      if (!this.canAccept(transfer)) return

      try {
        await store.dispatch("updateDeviceLocations", {
          id: transfer.id,
          hardware_id: transfer.hardwareIds,
          status: transfer.location_status,
          user: this.currentUserEmail,
          sender_organisation: transfer.sender_organisation,
          receiving_organisation: transfer.receiving_organisation,
          transfer_status: "Accepted",
        })

        await this.fetchTransfers()
      } catch (error) {
        console.error(error)
      }
    },
    async rejectTransfer(transfer) {
      if (!this.canModify(transfer)) return

      try {
        await store.dispatch("updateDeviceLocations", {
          id: transfer.id,
          hardware_id: transfer.hardwareIds,
          status: transfer.location_status,
          user: this.currentUserEmail,
          sender_organisation: transfer.sender_organisation,
          receiving_organisation: transfer.receiving_organisation,
          transfer_status: "Rejected",
        })

        await this.fetchTransfers()
      } catch (error) {
        console.error(error)
      }
    },
  },
}
</script>

<style>
.transfer-device-menu .v-list-item.transfer-device-option {
  min-height: 64px;
}

.transfer-device-menu .v-list-item.transfer-device-option + .v-list-item.transfer-device-option {
  border-top: 1px solid rgba(94, 86, 105, 0.14);
}

.theme--dark .transfer-device-menu .v-list-item.transfer-device-option + .v-list-item.transfer-device-option {
  border-top-color: rgba(231, 227, 252, 0.12);
}

.transfer-device-menu .v-list-item__subtitle {
  line-height: 1.3;
}
</style>
